import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import { tagItem } from '@synoptic/ui-kit/multi-tag/multi-tag.css.js';
import {
  MultiTag,
  MultiTagItem,
} from '@synoptic/ui-kit/multi-tag/multi-tag.js';
import clsx from 'clsx';
import { useState } from 'react';
import {
  emptyExcluded,
  excludedTag,
  includedTag,
  removalAlert,
  sectionBreak,
  streamField,
  tagLabelButton,
  targetsCount,
  tagRow,
  targetsFoot,
  targetsHint,
} from './custom-targets-section.css';
import { targetTag } from '@/components/streams/custom-stream/targets-modal.css';

interface StreamTargetsFieldProps {
  streamName: string;
  allTargets: string[];
  onAllTargetsChange: (targets: string[]) => void;
  /** What the stream has saved; anything else was added in this form. */
  savedTargets: string[];
  /** The subset this list uses; undefined means all of them. */
  explicitTargets?: string[];
  onExplicitTargetsChange: (targets: string[] | undefined) => void;
  /** 0 when the stream sets no quota. */
  quota: number;
  onBuyMore?: () => void;
  instruction?: string | null;
  targetValidationRegex?: string | null;
}

export const StreamTargetsField = ({
  streamName,
  allTargets,
  onAllTargetsChange,
  savedTargets,
  explicitTargets,
  onExplicitTargetsChange,
  quota,
  onBuyMore,
  instruction,
  targetValidationRegex,
}: StreamTargetsFieldProps) => {
  const [removed, setRemoved] = useState<string[]>([]);

  const usedHere = explicitTargets ?? allTargets;
  const usedHereSet = new Set(usedHere);

  const included = allTargets.filter((target) => usedHereSet.has(target));
  const excluded = allTargets.filter((target) => !usedHereSet.has(target));
  const ordered = [...included, ...excluded];
  const usedAll = allTargets.length > 0 && excluded.length === 0;
  const firstExcluded = excluded[0];
  // Nothing excluded: the heading trails the last included tag instead.
  const lastIncluded =
    excluded.length === 0 ? included[included.length - 1] : undefined;

  const setUsedHere = (next: string[]) =>
    // Every target is the default state, not a subset that holds them all.
    onExplicitTargetsChange(
      allTargets.length > 0 && next.length === allTargets.length
        ? undefined
        : next,
    );

  const toggle = (target: string) =>
    setUsedHere(
      usedHereSet.has(target)
        ? usedHere.filter((value) => value !== target)
        : [...usedHere, target],
    );

  const restore = () => {
    onAllTargetsChange([...allTargets, ...removed]);
    setRemoved([]);
  };

  // The caret sits after the included tags, so backspace drops the last of
  // those from the list. Only once none are left does it reach the excluded
  // ones, where removal means leaving the stream and has to be flagged.
  const onBackspace = () => {
    const last = included[included.length - 1];
    if (last) {
      toggle(last);
      return true;
    }
    const lastExcluded = excluded[excluded.length - 1];
    if (lastExcluded && savedTargets.includes(lastExcluded)) {
      setRemoved((current) =>
        current.includes(lastExcluded) ? current : [...current, lastExcluded],
      );
    }
    return false;
  };

  const onTagsChange = (next: string[]) => {
    const known = new Set(allTargets);
    const added = next.filter((target) => !known.has(target));
    onAllTargetsChange(next);
    if (added.length === 0) return;
    setRemoved((current) =>
      current.filter((target) => !added.includes(target)),
    );
    if (explicitTargets) {
      onExplicitTargetsChange([...explicitTargets, ...added]);
    }
  };

  return (
    <div className={streamField}>
      <MultiTag
        value={ordered}
        onChange={onTagsChange}
        label={streamName}
        placeholder={
          excluded.length > 0
            ? 'Type to add, or pick one below'
            : 'Type or paste targets to add…'
        }
        className={tagRow}
        maxItems={quota || undefined}
        regex={targetValidationRegex ?? undefined}
        onBackspace={onBackspace}
        TagItem={({ children, onRemove }) => {
          const target = String(children);

          if (usedHereSet.has(target)) {
            return (
              <>
                <button
                  type="button"
                  className={clsx(tagItem, targetTag, includedTag)}
                  aria-pressed="true"
                  onClick={() => toggle(target)}
                >
                  {target}
                </button>
                {target === lastIncluded ? (
                  <>
                    <span className={sectionBreak}>
                      excluded from this list
                    </span>
                    <span className={emptyExcluded}>
                      No excluded targets. Click one above to exclude it from
                      this list.
                    </span>
                  </>
                ) : null}
              </>
            );
          }

          return (
            <>
              {target === firstExcluded ? (
                <span className={sectionBreak}>excluded from this list</span>
              ) : null}
              <MultiTagItem
                className={clsx(targetTag, excludedTag)}
                onRemove={() => {
                  // Only a saved target has anything to lose.
                  if (savedTargets.includes(target)) {
                    setRemoved((current) =>
                      current.includes(target) ? current : [...current, target],
                    );
                  }
                  onRemove();
                }}
              >
                <button
                  type="button"
                  className={tagLabelButton}
                  aria-pressed="false"
                  onClick={() => toggle(target)}
                >
                  {target}
                </button>
              </MultiTagItem>
            </>
          );
        }}
      />
      <div className={targetsFoot}>
        <span className={targetsCount}>
          {usedHere.length} of {allTargets.length} used in this list
          {allTargets.length > 0 ? (
            <LinkButton
              size="small"
              onClick={() => setUsedHere(usedAll ? [] : allTargets)}
            >
              {usedAll ? 'Exclude all' : 'Use all'}
            </LinkButton>
          ) : null}
        </span>
        {quota > 0 ? (
          <span>
            {allTargets.length} / {quota}
            {onBuyMore ? (
              <>
                {' · '}
                <LinkButton size="small" onClick={onBuyMore}>
                  Buy more targets
                </LinkButton>
              </>
            ) : null}
          </span>
        ) : (
          <span>
            {allTargets.length} {allTargets.length === 1 ? 'target' : 'targets'}
          </span>
        )}
      </div>
      {instruction ? (
        <p className={targetsHint}>
          <strong>Author&apos;s instruction: </strong>
          {instruction}
        </p>
      ) : null}
      {removed.length > 0 ? (
        <div className={removalAlert} role="status">
          <span>
            {removed.join(', ')} {removed.length === 1 ? 'leaves' : 'leave'}{' '}
            {streamName} when you save, and stops reaching every list using it.
          </span>
          <LinkButton size="small" onClick={restore}>
            Restore
          </LinkButton>
        </div>
      ) : null}
    </div>
  );
};
